import { useEffect, useMemo, useState } from "react";
import { IconChevron } from "../control/ui/icons";
import { track } from "../analytics";
import type { ReactNode } from "react";
import type { View } from "@gooner/shared";
import {
  INFINITY_LIST,
  INFINITY_SECTION,
  MONITOR_TEMPLATES,
  WALL_LIST,
  builtinViewId, RESERVED_COLLECTION_IDS, VAULT_COLLECTION_ID, ownerScopedPlaylistId } from "@gooner/shared";
import { api, userApi } from "../lib/api";
import { collectionView, randomPlayLayout } from "../lib/playCollection";
import { trackVaultPlayed } from "../lib/vault";
import { cx } from "../control/ui/primitives";
import { useSession } from "../lib/session";
import { useGoldGate } from "../lib/gold";
import { AuthModal } from "../control/components/AuthModal";
import { WallGlyph } from "./WallGlyph";
import { InfinityGlyph } from "./InfinityGlyph";
import { NewBadge } from "./NewBadge";
import { MASK } from "../lib/replayMask";

/**
 * THE "What's playing" picker — your views, your collections, quick setups, the
 * editors' collections — shared by the two surfaces that can change what a screen
 * plays: the remote (aiming another device) and the screen itself (its Now Showing
 * bubble opens this over the video). One component, because the moment they were two
 * lists they would offer different things.
 *
 * It OWNS ITS DATA: views, public collections and this browser's playlists are fetched
 * on mount and again when the session changes — the sheet mounts when opened, so every
 * open reads fresh. Quick setups are static and need no account; everything else degrades
 * to absence when a fetch fails, so the sheet works signed out and offline-ish rather than
 * gating. Signed OUT, "Your views" and "Your collections" still stand, each with a
 * sign-in prompt in place of its list — the shape of the sheet tells you what an account
 * buys, and the modal opens right here so the lists fill in without leaving.
 *
 * It does NOT own the pointing. `onPoint` is the host's: the remote assigns the aimed
 * device and nudges it over the control socket; a screen assigns itself and does what
 * the reload command would have told it to. Collections are the exception it half-owns:
 * a tap mints the temp view here (random layout, every region streaming the collection
 * — the same shape /play/collection builds) and THEN hands the minted id to onPoint.
 * No device is ever opened here; minting a screen is /play's job, not the picker's.
 */

/** One row of the picker, as data: what the arrows cycle and the sheet lists. */
interface PlayableEntry {
  kind: "wall" | "infinity" | "view" | "collection" | "quick_setup";
  /** A view id, a builtin view id, or a collection (playlist) id — "faps" included. */
  id: string;
  name: string;
  icon: string;
}

/**
 * The picker's data, extracted so the sheet and the screen's Left/Right cycling read
 * the SAME list in the SAME order — two copies is how the arrows and the sheet would
 * drift into offering different things. `entries` is the sheet flattened top to bottom.
 */
export function useWhatsPlayingEntries(): {
  views: View[];
  mine: Array<{ id: string; name: string }>;
  others: Array<{ id: string; name: string }>;
  entries: PlayableEntry[];
} {
  const [views, setViews] = useState<View[]>([]);
  const [publicCols, setPublicCols] = useState<Array<{ id: string; name: string }>>([]);
  const [mine, setMine] = useState<Array<{ id: string; name: string }>>([]);
  const user = useSession((s) => s.user);

  // Re-run when the session changes, so a sign-in from the prompt below fills the lists
  // in place rather than needing the sheet reopened.
  useEffect(() => {
    void api.views.list().then((r) => setViews(r.views)).catch(() => setViews([]));
    void api
      .listCollections()
      .then((r) => setPublicCols(r.collections.map(({ id, name }) => ({ id, name }))))
      .catch(() => setPublicCols([]));
    // Signed out there are no playlists to have, so don't ask. The catch below still
    // stands for a real failure, but a signed-out visitor is not one: /api/playlists is
    // account-gated, so asking anyway spent a guaranteed 401 on every page load and put a
    // red line in the console of anybody debugging something else. DisplayApp holds this
    // hook unconditionally — the arrows need the list, not just the sheet — so on /screen
    // that was every visit. The effect re-runs on `user`, so signing in fills it in place.
    if (!user) {
      setMine([]);
      return;
    }
    void userApi
      .listPlaylists()
      .then((r) => setMine(r.playlists.map(({ id, name }) => ({ id, name }))))
      .catch(() => setMine([]));
  }, [user]);

  // Everything public that is NOT yours — the editors' shelf. Yours show under their
  // own header, so a public collection of your own appears once, up there.
  const others = useMemo(() => {
    const yours = new Set(mine.map((c) => c.id));
    return publicCols.filter((c) => !yours.has(c.id));
  }, [publicCols, mine]);

  const entries = useMemo<PlayableEntry[]>(
    () => [
      ...views.map((v) => ({ kind: "view" as const, id: v.id, name: v.name, icon: v.icon || "🖥️" })),
      ...mine.map((c) => ({ kind: "collection" as const, id: c.id, name: c.name, icon: "🎞️" })),
      // The walls, where the sheet puts them (after your own things), and in their own
      // order — so from the Community Wall one press Right is the Premium Wall. They
      // used to lead, one wall as a lone row above everything; a shelf of two is a
      // section, and the arrows read the sheet top to bottom.
      ...WALL_LIST.map((w) => ({ kind: "wall" as const, id: w.viewId, name: w.name, icon: w.icon })),
      // The scrolls after the walls, where their section sits: one press Right from the
      // Premium Wall is the endless one.
      ...INFINITY_LIST.map((d) => ({ kind: "infinity" as const, id: d.viewId, name: d.name, icon: d.icon })),
      ...MONITOR_TEMPLATES.map((t) => ({
        kind: "quick_setup" as const,
        id: builtinViewId(t.id),
        name: t.name,
        icon: t.emoji,
      })),
      ...others.map((c) => ({ kind: "collection" as const, id: c.id, name: c.name, icon: "🎞️" })),
    ],
    [views, mine, others],
  );

  return { views, mine, others, entries };
}

/**
 * Point a screen at a collection: a RANDOM layout every time, every region streaming
 * the collection. Shared by the sheet's tap and the arrows' landing, so the faps
 * special case and the analytics live once.
 */
export async function pointCollectionEntry(
  c: { id: string; name: string },
  onPoint: (viewId: string, label: string) => void | Promise<void>,
  onMinted?: (viewId: string, meta: { name: string; icon: string; collectionId: string }) => void,
  /** The tapping viewer's region ceiling, so the random layout stays within their tier —
   *  a non-Gold tap must not land a gold layout. Infinity (the default) picks from all. */
  maxRegions = Number.POSITIVE_INFINITY,
): Promise<void> {
  // "faps" and "uploads" are the reserved ids of the two VIRTUAL collections — per-user,
  // no playlist rows. The public resolver can only answer them scoped to an owner, so
  // the tap trades each for its `<id>:<uid>` form (we are the owner: the list came from
  // our bearer). For uploads that owner-scoped id is then answered ONLY for the owner's
  // own screen — a stranger who lands on this view gets an empty region, by design.
  const playlistId = RESERVED_COLLECTION_IDS.has(c.id)
    ? (ownerScopedPlaylistId(c.id, (await userApi.me()).user.id) ?? c.id)
    : c.id;
  if (c.id === VAULT_COLLECTION_ID) trackVaultPlayed("picker");
  const v = collectionView(playlistId, randomPlayLayout(maxRegions), c.name);
  const { view } = await api.views.createTemp({
    name: v.name,
    icon: "🎞️",
    aspect: v.aspect,
    regions: v.regions,
  });
  onMinted?.(view.id, { name: c.name, icon: "🎞️", collectionId: c.id });
  track("whats_playing_picked", { kind: "collection" });
  await onPoint(view.id, c.name);
}

export function WhatsPlayingSheet({
  showing,
  onPoint,
  onClose,
  onMinted,
}: {
  /** What the aimed screen is playing now, for the active highlight. */
  showing: string | null;
  /** Point the aimed screen at a view — the host owns how. */
  onPoint: (viewId: string, label: string) => void | Promise<void>;
  onClose: () => void;
  /** A collection tap minted this temp view. Hosts that resolve names from lists (the
   *  remote's rail, a screen's cycle cursor) remember it here; a screen learns the
   *  name from its own socket. */
  onMinted?: (viewId: string, meta: { name: string; icon: string; collectionId: string }) => void;
}) {
  const { views, mine, others } = useWhatsPlayingEntries();
  const user = useSession((s) => s.user);
  const { maxRegions } = useGoldGate();
  // The sign-in modal opened from a section prompt. A portal at z-60, above this sheet's
  // z-40 scrim, so it simply layers on; closing it (signed in or not) returns you here.
  const [auth, setAuth] = useState<"login" | null>(null);

  const pointCollection = (c: { id: string; name: string }) =>
    pointCollectionEntry(c, onPoint, onMinted, maxRegions);

  // Which sections exist varies (signed out has no collections, a fresh browser no
  // views), so "first section" is decided at render: the first header rendered skips
  // the mt-4 the rest use to separate themselves from the section above.
  let firstSection = true;
  const headerCls = () => {
    const cls = firstSection
      ? "mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500"
      : "mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wide text-zinc-500";
    firstSection = false;
    return cls;
  };

  return (
    <BottomSheet title="What's playing" onClose={onClose}>
      {/* Yours first — the views you built, then your collections — because what you
          made is what you reach for. The walls follow as a shelf of their own, quick
          setups for a thumb with no account, and the editors' collections close the list. */}
      {views.length > 0 ? (
        <Shelf header={<p className={headerCls()}>Your views</p>} items={views} keyOf={(v) => v.id}>
          {(v) => (
            <SheetBtn
              active={showing === v.id}
              onClick={() => {
                track("whats_playing_picked", { kind: "view" });
                void onPoint(v.id, v.name);
              }}
            >
              <span aria-hidden>{v.icon || "🖥️"}</span>
              <span className="truncate" {...MASK}>{v.name}</span>
            </SheetBtn>
          )}
        </Shelf>
      ) : (
        <>
          <p className={headerCls()}>Your views</p>
          {user ? (
            <p className="text-sm text-zinc-500">No saved views yet.</p>
          ) : (
            <SignInPrompt onClick={() => setAuth("login")}>Sign in to select your views</SignInPrompt>
          )}
        </>
      )}
      {/* Collection rows are never "active" — a tap mints a FRESH random layout, so
          there is nothing stable to match the screen's showing id against, and that
          is the feature. */}
      {mine.length > 0 ? (
        <Shelf header={<p className={headerCls()}>Your collections</p>} items={mine} keyOf={(c) => c.id}>
          {(c) => (
            <SheetBtn onClick={() => void pointCollection(c)}>
              <span aria-hidden>🎞️</span>
              <span className="truncate" {...MASK}>{c.name}</span>
            </SheetBtn>
          )}
        </Shelf>
      ) : (
        <>
          <p className={headerCls()}>Your collections</p>
          {user ? (
            <p className="text-sm text-zinc-500">No collections yet.</p>
          ) : (
            <SignInPrompt onClick={() => setAuth("login")}>Sign in to select your collections</SignInPrompt>
          )}
        </>
      )}
      {/* The walls — the shared boards, the same for every account and no account. The
          Community Wall used to be a lone full-width row above everything ("a PLACE,
          singular"); two places are a shelf, and a shelf is a section. Every row reads
          from WALL_DEFS, so a third wall is a row there and nothing here. */}
      <Shelf header={<p className={headerCls()}>Walls</p>} items={WALL_LIST} keyOf={(w) => w.id}>
        {(w) => (
          <SheetBtn
            active={showing === w.viewId}
            onClick={() => {
              track("whats_playing_picked", { kind: "wall", wall: w.id });
              void onPoint(w.viewId, w.name);
            }}
          >
            <WallGlyph wallId={w.id} />
            <span className="truncate">{w.name}</span>
          </SheetBtn>
        )}
      </Shelf>
      {/* The scrolls — their own section, because a scroll is not a wall (nothing is
          owned, nothing is authored) and not a quick setup (it is not a layout at all).
          Every row reads from INFINITY_DEFS, so a third scroll is a row there and
          nothing here. */}
      <Shelf header={<p className={headerCls()}>{INFINITY_SECTION}</p>} items={INFINITY_LIST} keyOf={(d) => d.id}>
        {(d) => (
          <SheetBtn
            active={showing === d.viewId}
            onClick={() => {
              track("whats_playing_picked", { kind: "infinity", scroll: d.id });
              void onPoint(d.viewId, d.name);
            }}
          >
            <InfinityGlyph infinityId={d.id} />
            <span className="truncate">{d.name}</span>
            {Date.now() < d.newUntil ? <NewBadge /> : null}
          </SheetBtn>
        )}
      </Shelf>
      <Shelf header={<p className={headerCls()}>Quick setups</p>} items={MONITOR_TEMPLATES} keyOf={(t) => t.id}>
        {(t) => (
          <SheetBtn
            active={showing === builtinViewId(t.id)}
            onClick={() => {
              track("whats_playing_picked", { kind: "quick_setup" });
              void onPoint(builtinViewId(t.id), t.name);
            }}
          >
            <span aria-hidden>{t.emoji}</span>
            <span className="truncate">{t.name}</span>
          </SheetBtn>
        )}
      </Shelf>
      {others.length > 0 ? (
        <>
          <Shelf header={<p className={headerCls()}>Collections</p>} items={others} keyOf={(c) => c.id}>
            {(c) => (
              <SheetBtn onClick={() => void pointCollection(c)}>
                <span aria-hidden>🎞️</span>
                <span className="truncate" {...MASK}>{c.name}</span>
              </SheetBtn>
            )}
          </Shelf>
        </>
      ) : null}
      {auth ? (
        <AuthModal
          initialMode={auth}
          reason="Sign in to pick from your own views and collections."
          onClose={() => setAuth(null)}
        />
      ) : null}
    </BottomSheet>
  );
}

// The scrolls' NEW! sunset lives on each row (InfinityDef.newUntil): a scroll is new for
// a month after ITS launch, so the badge moves to the newest rows instead of lighting
// up the whole section until the last one's date.

/** Rows a shelf shows before it pages: enough to scan, not enough to scroll past. */
const SHELF_ROWS = 4;
/** The width at which the sheet is a wide desk rather than a phone, and a row can hold
 *  four tiles instead of two — Tailwind's `lg`, matched here because paging has to know
 *  the column count the CSS is about to use. */
const SHELF_WIDE_PX = 1024;

function useShelfCols(): number {
  const [wide, setWide] = useState(
    () => typeof window !== "undefined" && window.matchMedia(`(min-width: ${SHELF_WIDE_PX}px)`).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${SHELF_WIDE_PX}px)`);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide ? 4 : 2;
}

/**
 * ONE SHELF for every section of the sheet: a header, a grid of tiles — two across on a
 * phone, four on a wide screen — and, past SHELF_ROWS rows, pages with a small pair of
 * arrows beside the header. A shelf that fits shows no arrows at all. Paging rather than
 * a taller sheet because the sheet is thumb furniture: a section that runs past the fold
 * is a section nobody reaches the bottom of.
 */
function Shelf<T>({
  header,
  items,
  keyOf,
  children,
}: {
  header: ReactNode;
  items: ReadonlyArray<T>;
  keyOf: (item: T) => string;
  children: (item: T) => ReactNode;
}) {
  const cols = useShelfCols();
  const pageSize = cols * SHELF_ROWS;
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  // Clamped at render, not in an effect: a list that shrinks (a deleted view) or a
  // window that widens must not strand the shelf on a page that no longer exists.
  const at = Math.min(page, pages - 1);
  const shown = items.slice(at * pageSize, (at + 1) * pageSize);
  const arrow = "rounded-md p-0.5 text-zinc-500 hover:text-zinc-200 disabled:opacity-30 disabled:hover:text-zinc-500";
  return (
    <>
      <div className="flex items-end justify-between gap-2">
        {header}
        {pages > 1 ? (
          <div className="mb-2 flex items-center gap-1 text-[11px] tabular-nums text-zinc-500">
            <button type="button" className={arrow} aria-label="Previous page" disabled={at === 0} onClick={() => setPage(at - 1)}>
              <IconChevron width={14} height={14} className="rotate-90" />
            </button>
            <span>
              {at + 1}/{pages}
            </span>
            <button type="button" className={arrow} aria-label="Next page" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>
              <IconChevron width={14} height={14} className="-rotate-90" />
            </button>
          </div>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {shown.map((item) => (
          <div key={keyOf(item)} className="contents">
            {children(item)}
          </div>
        ))}
      </div>
    </>
  );
}

/** A section's stand-in when there is nothing to list because nobody is signed in: the
 *  same tile shape as the choices, spanning the row, reading as the one tap that fixes it. */
function SignInPrompt({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-xl border border-dashed border-[var(--color-edge)] px-3 py-3 text-left text-sm font-medium text-zinc-400 transition-transform active:scale-95"
    >
      <span aria-hidden>🔒</span>
      <span className="underline underline-offset-2">{children}</span>
    </button>
  );
}

/**
 * The bottom sheet both pickers ride in — thumb-reach on a phone, dismissed by the
 * scrim or the ✕. Extracted with the picker from the Goontroller, which still uses it
 * for its own screen-chooser sheet.
 */
export function BottomSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  // Escape closes, like every other dismissible surface here (ConfirmDialog, ClipViewer,
  // TagBubble). The sheet is thumb-reach furniture, but it is also what a desktop browser
  // narrowed past `md:` gets, and there a keyboard is the obvious way out.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        // `dvh`, not `vh`: on iOS `70vh` measures against the LARGE viewport, so with the
        // address bar showing the sheet is taller than the 70% it asks for.
        className="max-h-[70dvh] w-full overflow-y-auto rounded-t-2xl border-t border-[var(--color-edge)] bg-[var(--color-panel)] p-4 pb-8 shadow-2xl"
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--color-edge)]" />
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">{title}</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-zinc-400">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function SheetBtn({
  children,
  onClick,
  active,
}: {
  children: ReactNode;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "relative flex items-center gap-2 rounded-xl border px-3 py-3 text-left text-sm font-semibold transition-transform active:scale-95",
        active
          ? "border-[var(--color-accent)] bg-[var(--color-accent)]/15 text-white"
          : "border-[var(--color-edge)] bg-[var(--color-ink)] text-zinc-200",
      )}
    >
      {children}
    </button>
  );
}
